import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  type Collision,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  KanbanCardContent,
} from "@/components/kanban/KanbanCard";
import { Button } from "@/components/ui/button";
import {
  KanbanColumn,
  statusFromKanbanColumnId,
} from "@/components/kanban/KanbanColumn";
import type { KanbanColumnViews } from "@/hooks/use-kanban-tasks";
import type { BulkTaskRequest, BulkTaskResult } from "@/hooks/use-kanban-tasks";
import { useMarqueeSelection } from "@/hooks/use-marquee-selection";
import { TASK_STATUS_LABEL_KEYS } from "@/lib/task-status";
import {
  TASK_STATUSES,
  type Tag,
  type Task,
  type TaskStatus,
  type TaskTitleSource,
  type TaskUpdateRequest,
  type TenantUser,
} from "@/lib/project-types";
import { cn } from "@/lib/utils";

type StatusChangeResult = boolean | Task | null | void;

export interface KanbanBoardProps {
  columns: KanbanColumnViews;
  /** The owner performs the optimistic cache move and persists only status. */
  onStatusChange: (
    taskId: string,
    newStatus: TaskStatus,
  ) => StatusChangeResult | Promise<StatusChangeResult>;
  onBulkAction?: (request: BulkTaskRequest) => Promise<BulkTaskResult>;
  onCardClick: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask?: (task: Task) => void;
  hierarchySupported?: boolean;
  onCreateTask: (
    title: string,
    status: TaskStatus,
    assignedTo?: string | null,
    tagIds?: string[],
    dueDate?: string | null,
    titleSource?: TaskTitleSource,
  ) => unknown | Promise<unknown>;
  onUpdateTask?: (
    taskId: string,
    updates: TaskUpdateRequest,
  ) => Promise<boolean>;
  allTags?: Tag[];
  onUpdateTags?: (taskId: string, tagIds: string[]) => Promise<boolean>;
  onCreateTag?: (name: string, color?: string) => Promise<Tag | null>;
  onArchiveTag?: (tagId: string) => Promise<boolean>;
  onRestoreTag?: (tagId: string) => Promise<boolean>;
  onLoadMore: (status: TaskStatus) => void | Promise<void>;
  onRetryColumn: (status: TaskStatus) => void | Promise<void>;
  onRevealPending: (status: TaskStatus) => void;
  onColumnAtTopChange: (status: TaskStatus, isAtTop: boolean) => void;
  /** Shared vertical viewport for pagination and new-task buffering. */
  scrollContainerRef?: RefObject<HTMLElement | null>;
  tenantUsers?: TenantUser[];
  defaultAssigneeId?: string | null;
  dueDateSupported?: boolean;
  timezone?: string;
  agentName?: string;
  agentIdForTask?: (task: Task) => string | undefined;
  defaultAgentId?: string | null;
  agentNameForTask?: (task: Task) => string | undefined;
  activeTaskId?: string | null;
  scrollToTaskId?: string | null;
  /** Restore keyboard focus after a drop only while task detail is open. */
  restoreFocusOnDrop?: boolean;
  /** Opens the Open column's inline task form exactly once. */
  shouldOpenCreateForm?: boolean;
  /** Called after the Open column has consumed the create-form signal. */
  onCreateFormOpened?: () => void;
  compact?: boolean;
  className?: string;
}

/** Resolve a DnD target to a fixed status column. Exported for tests. */
export function kanbanDropStatus(
  overId: string,
  tasks: Task[],
): TaskStatus | null {
  const columnStatus = statusFromKanbanColumnId(overId);
  if (columnStatus) return columnStatus;
  return tasks.find((task) => task.id === overId)?.status ?? null;
}

/** Commit one group drop through one batch callback, preserving rendered order. */
export async function commitKanbanDrop(
  activeId: string,
  overId: string,
  selectedIds: ReadonlySet<string>,
  tasks: Task[],
  onStatusChange: KanbanBoardProps["onStatusChange"],
  onBulkAction?: KanbanBoardProps["onBulkAction"],
): Promise<BulkTaskResult | null> {
  const task = tasks.find((item) => item.id === activeId);
  const destinationStatus = kanbanDropStatus(overId, tasks);
  if (!task || !destinationStatus) return null;
  const group = selectedIds.has(activeId)
    ? tasks.filter((item) => selectedIds.has(item.id))
    : [task];
  if (group.length > 1 && onBulkAction) {
    const moving = group.filter((item) => item.status !== destinationStatus);
    if (!moving.length) return null;
    return onBulkAction({
      task_ids: moving.map((item) => item.id),
      action: "set_status", status: destinationStatus,
    });
  }
  if (task.status !== destinationStatus) await onStatusChange(task.id, destinationStatus);
  return null;
}

/**
 * A pointer over a card is also over its fixed-status lane. Prefer that lane
 * so a card beneath the pointer cannot override the intended drop target.
 */
export function prioritizeKanbanColumnCollision(
  collisions: Collision[],
): Collision[] {
  const columnCollision = collisions.find(
    (collision) => statusFromKanbanColumnId(String(collision.id)) !== null,
  );
  return columnCollision ? [columnCollision] : collisions;
}

export const kanbanCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = prioritizeKanbanColumnCollision(pointerWithin(args));
  return pointerCollisions.length > 0
    ? pointerCollisions
    : closestCorners(args);
};

/**
 * Fixed-status board with grid-stretched, full-height lanes. Dragging changes status
 * only; exact card ordering remains the server's created_at/id order because
 * tasks have no rank.
 */
export function KanbanBoard({
  columns,
  onStatusChange,
  onBulkAction,
  onCardClick,
  onDeleteTask,
  onAddSubtask,
  hierarchySupported = false,
  onCreateTask,
  onUpdateTask,
  allTags,
  onUpdateTags,
  onCreateTag,
  onArchiveTag,
  onRestoreTag,
  onLoadMore,
  onRetryColumn,
  onRevealPending,
  onColumnAtTopChange,
  scrollContainerRef,
  tenantUsers,
  defaultAssigneeId,
  dueDateSupported = false,
  timezone = "Europe/Zurich",
  agentName,
  agentIdForTask,
  defaultAgentId,
  agentNameForTask,
  activeTaskId = null,
  scrollToTaskId = null,
  restoreFocusOnDrop = false,
  shouldOpenCreateForm = false,
  onCreateFormOpened,
  compact = false,
  className,
}: KanbanBoardProps) {
  const { t } = useTranslation();
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [activeDragTask, setActiveDragTask] = useState<Task | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [draggedGroupIds, setDraggedGroupIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<TaskStatus>("in_progress");
  const [bulkTagId, setBulkTagId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);
  const anchorIdRef = useRef<string | null>(null);
  const lastAtTopRef = useRef<boolean | null>(null);
  const { rectangle, onPointerDown } = useMarqueeSelection(boardRef, selectedTaskIds, setSelectedTaskIds);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const tasks = useMemo(
    () => TASK_STATUSES.flatMap((status) => columns[status].tasks),
    [columns],
  );
  const taskById = useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks],
  );

  const showBulkResult = (result: BulkTaskResult, toastId: string | number) => {
    const message = result.unsupported ? t("projects:bulkUnsupported")
      : result.error ? t("projects:bulkFailed")
        : t("projects:bulkResult", { succeeded: result.succeeded.length, failed: result.failed.length });
    const notify = result.unsupported || result.error || result.failed.length ? toast.error : toast.success;
    notify(message, { id: toastId });
  };

  useEffect(() => {
    const viewport = scrollContainerRef?.current;
    const reportScroll = () => {
      const isAtTop = (viewport?.scrollTop ?? window.scrollY) <= 8;
      if (lastAtTopRef.current === isAtTop) return;
      lastAtTopRef.current = isAtTop;
      for (const status of TASK_STATUSES) onColumnAtTopChange(status, isAtTop);
    };
    reportScroll();
    const target = viewport ?? window;
    target.addEventListener("scroll", reportScroll, { passive: true });
    return () => target.removeEventListener("scroll", reportScroll);
  }, [onColumnAtTopChange, scrollContainerRef]);

  const clearActiveDrag = () => {
    setDraggedTaskId(null);
    setActiveDragTask(null);
    setDraggedGroupIds([]);
  };

  const handleSelectionClick = (task: Task, event: MouseEvent | KeyboardEvent): boolean => {
    if (!event.ctrlKey && !event.metaKey && !event.shiftKey) return false;
    if (event.shiftKey && anchorIdRef.current) {
      const anchor = tasks.findIndex((item) => item.id === anchorIdRef.current);
      const target = tasks.findIndex((item) => item.id === task.id);
      if (anchor >= 0 && target >= 0) {
        setSelectedTaskIds((current) => new Set([
          ...current,
          ...tasks.slice(Math.min(anchor, target), Math.max(anchor, target) + 1).map((item) => item.id),
        ]));
      }
    } else {
      setSelectedTaskIds((current) => {
        const next = new Set(current);
        if (next.has(task.id)) next.delete(task.id); else next.add(task.id);
        return next;
      });
      anchorIdRef.current = task.id;
    }
    return true;
  };

  const runBulkAction = async (action: BulkTaskRequest["action"]) => {
    if (!onBulkAction || bulkBusy) return;
    const ids = tasks.filter((task) => selectedTaskIds.has(task.id)).map((task) => task.id);
    if (!ids.length) return;
    if (action === "cancel" && !window.confirm(t("projects:bulkDeleteConfirm", { count: ids.length }))) return;
    setBulkBusy(true);
    const toastId = toast.loading(t("projects:bulkProgress", { count: ids.length }));
    const result = await onBulkAction({
      task_ids: ids,
      action,
      ...(action === "set_status" ? { status: bulkStatus } : {}),
      ...(action === "add_tags" || action === "remove_tags" ? { tag_ids: [bulkTagId] } : {}),
    });
    setBulkBusy(false);
    showBulkResult(result, toastId);
    if (result.succeeded.length) setSelectedTaskIds((current) => {
      const next = new Set(current);
      result.succeeded.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const taskId = String(event.active.id);
    setDraggedTaskId(taskId);
    setActiveDragTask(taskById.get(taskId) ?? null);
    setDraggedGroupIds(selectedTaskIds.has(taskId)
      ? tasks.filter((task) => selectedTaskIds.has(task.id)).map((task) => task.id)
      : [taskId]);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const groupIds = draggedGroupIds;
    clearActiveDrag();
    if (!over) return;
    let toastId: string | number | undefined;
    if (groupIds.length > 1) {
      setBulkBusy(true);
      toastId = toast.loading(t("projects:bulkProgress", { count: groupIds.length }));
    }
    const result = await commitKanbanDrop(
      String(active.id), String(over.id), new Set(groupIds), tasks,
      onStatusChange, onBulkAction,
    );
    if (!result) {
      setBulkBusy(false);
      if (toastId !== undefined) toast.dismiss(toastId);
      return;
    }
    setBulkBusy(false);
    if (toastId !== undefined) showBulkResult(result, toastId);
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      result.succeeded.forEach((id) => next.delete(id));
      return next;
    });
  };

  return (
    <section
      className={cn("relative flex min-w-0 flex-1 flex-col", className)}
      aria-label={t("projects:projectTasks")}
      data-testid="kanban-board"
    >
      {selectedTaskIds.size > 0 && onBulkAction && (
        <div className="absolute inset-x-4 top-4 z-40 flex flex-wrap items-center gap-2 rounded-md border bg-background p-2 text-sm shadow-lg" role="toolbar" aria-label={t("projects:bulkToolbar")}>
          <span aria-live="polite">{t("projects:bulkSelectedCount", { count: selectedTaskIds.size })}</span>
          <select className="h-8 rounded-md border bg-background px-2" aria-label={t("projects:bulkMoveTo")} value={bulkStatus} onChange={(event) => setBulkStatus(event.target.value as TaskStatus)}>
            {TASK_STATUSES.map((status) => <option key={status} value={status}>{t(TASK_STATUS_LABEL_KEYS[status])}</option>)}
          </select>
          <Button type="button" size="sm" disabled={bulkBusy} onClick={() => void runBulkAction("set_status")}>{t("projects:bulkMoveTo")}</Button>
          <select className="h-8 rounded-md border bg-background px-2" aria-label={t("projects:bulkTag")} value={bulkTagId} onChange={(event) => setBulkTagId(event.target.value)}>
            <option value="">{t("projects:bulkTag")}</option>
            {allTags?.filter((tag) => !tag.is_archived).map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
          </select>
          <Button type="button" variant="outline" size="sm" disabled={bulkBusy || !bulkTagId} onClick={() => void runBulkAction("add_tags")}>{t("projects:bulkAddTag")}</Button>
          <Button type="button" variant="outline" size="sm" disabled={bulkBusy || !bulkTagId} onClick={() => void runBulkAction("remove_tags")}>{t("projects:bulkRemoveTag")}</Button>
          <Button type="button" variant="destructive" size="sm" disabled={bulkBusy} onClick={() => void runBulkAction("cancel")}>{t("projects:bulkDelete")}</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedTaskIds(new Set())}>{t("projects:bulkClear")}</Button>
        </div>
      )}
      <DndContext
        sensors={sensors}
        autoScroll
        accessibility={{ restoreFocus: restoreFocusOnDrop }}
        collisionDetection={kanbanCollisionDetection}
        onDragStart={handleDragStart}
        onDragCancel={clearActiveDrag}
        onDragEnd={(event) => {
          void handleDragEnd(event);
        }}
      >
        <div
          ref={boardRef}
          onPointerDown={onPointerDown}
          className={cn("min-w-0 flex-none overflow-x-auto p-4", rectangle && "select-none")}
          data-testid="kanban-board-scroll"
        >
          <div
            className={cn(
              "grid min-h-[32rem] w-max grid-cols-[repeat(5,18rem)] items-stretch gap-4",
              compact && "pb-1",
            )}
          >
            {TASK_STATUSES.map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                column={columns[status]}
                onCardClick={onCardClick}
                selectedTaskIds={selectedTaskIds}
                onSelectionClick={handleSelectionClick}
                onDeleteTask={onDeleteTask}
                onAddSubtask={onAddSubtask}
                hierarchySupported={hierarchySupported}
                onCreateTask={onCreateTask}
                onUpdateTask={onUpdateTask}
                allTags={allTags}
                onUpdateTags={onUpdateTags}
                onCreateTag={onCreateTag}
                onArchiveTag={onArchiveTag}
                onRestoreTag={onRestoreTag}
                onLoadMore={onLoadMore}
                onRetry={onRetryColumn}
                onRevealPending={onRevealPending}
                scrollContainerRef={scrollContainerRef}
                tenantUsers={tenantUsers}
                defaultAssigneeId={defaultAssigneeId}
                dueDateSupported={dueDateSupported}
                timezone={timezone}
                agentName={agentName}
                agentIdForTask={agentIdForTask}
                defaultAgentId={defaultAgentId}
                agentNameForTask={agentNameForTask}
                activeTaskId={activeTaskId}
                scrollToTaskId={scrollToTaskId}
                shouldOpenCreateForm={
                  status === "open" && shouldOpenCreateForm
                }
                onCreateFormOpened={
                  status === "open" ? onCreateFormOpened : undefined
                }
                className={draggedTaskId ? "select-none" : undefined}
              />
            ))}
          </div>
        </div>
        <DragOverlay>
          {activeDragTask && draggedGroupIds.length > 1 ? (
            <div className="rounded-lg border bg-background p-4 shadow-xl">{t("projects:bulkDragCount", { count: draggedGroupIds.length })}</div>
          ) : activeDragTask && (
            <KanbanCardContent
              task={activeDragTask}
              agentName={
                agentNameForTask?.(activeDragTask) ?? agentName
              }
              agentId={agentIdForTask?.(activeDragTask)}
              defaultAgentId={defaultAgentId}
              onClick={() => {}}
              onDeleteTask={() => {}}
              tenantUsers={tenantUsers}
              dueDateSupported={dueDateSupported}
              timezone={timezone}
              overlay
            />
          )}
        </DragOverlay>
      </DndContext>
      {rectangle && <div className="pointer-events-none fixed z-50 border border-primary bg-primary/15" style={rectangle} aria-hidden="true" />}
    </section>
  );
}
